import React from 'react'
import styled from '@emotion/styled'
import { Divider, Stack, Typography } from '@mui/material'
import { Inputs } from '../../../containers/facilityReservationTodayConditionInput/facilityReservationTodayConditionInputService'
import { Control } from 'react-hook-form'
import { GetTodayConditionInputDetailDto } from '../../../dataAccess/webApi/dto/reservationsDto'
import { GContainer, GItem } from '../common/grids'
import { TitleLabel } from '../common/titleLabel'
import { AttentionLabel } from '../common/attentionLabel'
import { Select } from '../common/inputs/select'
import { NumberBox } from '../common/inputs/numberBox'
import { TimePicker } from '../common/inputs/timePicker'
import { TextBox } from '../common/inputs/textBox'
import { RadioButtonGroup } from '../common/inputs/radioButtonGroup'
import { PostDecodeMstDto } from '../../../dataAccess/webApi/dto/decodeMstDto'
import {
  reservationBrestFeedingDetail,
  reservationEatingMethod,
  todayConditionAmountOfFood,
  todayConditionInputFieldUsage,
} from '../../../containers/common/constant/classification'
import { translate } from '../../../i18n'

const SectionText = styled(Typography)(() => ({
  fontSize: '21px',
  marginTop: '10px',
}))

const BlackDivider = styled(Divider)(() => ({
  borderColor: '#000',
  marginBottom: '20px',
}))

type MealSecProps = {
  control: Control<Inputs>
  todayConditionInputFieldsInfo: GetTodayConditionInputDetailDto['todayConditionInputFieldsInfo']
  eatingMethodDecodeMst: PostDecodeMstDto[]
  amountOfFoodDecodeMst: PostDecodeMstDto[]
  brestFeedingDetailDecodeMst: PostDecodeMstDto[]
  isMeal: boolean
  eatingMethod: string | null
  breakfast: string | null
  lunch: string | null
  snack: string | null
  eatingMethodDetail: string | null
  yesNos: {
    value: string
    label: string
  }[]
}

// 食事セクション
const MealSec: React.FC<MealSecProps> = (props) => {
  const {
    control,
    todayConditionInputFieldsInfo,
    eatingMethodDecodeMst,
    amountOfFoodDecodeMst,
    brestFeedingDetailDecodeMst,
    isMeal,
    eatingMethod,
    breakfast,
    lunch,
    snack,
    eatingMethodDetail,
    yesNos,
  } = props

  // 栄養方法用の選択肢オプションを編集
  const eatingMethodValueLabels = eatingMethodDecodeMst.map((item) => {
    return {
      value: item.code,
      label: item.name,
    }
  })

  // 食事量用の選択肢オプションを編集
  const amountOfFoodValueLabels = amountOfFoodDecodeMst.map((item) => {
    return {
      value: item.code,
      label: item.name,
    }
  })

  // 栄養方法詳細用の選択肢オプションを編集
  const brestFeedingDetailValueLabels = brestFeedingDetailDecodeMst.map((item) => {
    return {
      value: item.code,
      label: item.name,
    }
  })

  return (
    <GItem xs={12}>
      {/* セクションタイトル部分 */}
      <GContainer>
        <GItem xs={12}>
          <SectionText>{translate('facilityReservationTodayConditionInput.section.meal')}</SectionText>
          <BlackDivider />
        </GItem>
      </GContainer>
      {/* フォーム部分 */}
      <Stack spacing={2}>
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.meal !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 登園前に食事をしましたか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.meal === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.meal')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'meal'}
                      label={translate('facilityReservationTodayConditionInput.form.label.meal')}
                      row
                      required={todayConditionInputFieldsInfo.meal === todayConditionInputFieldUsage.necessary}
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 食事時刻 */}
              {isMeal && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.meal === todayConditionInputFieldUsage.necessary && isMeal && (
                      <AttentionLabel>*</AttentionLabel>
                    )}
                    {translate('facilityReservationTodayConditionInput.form.label.mealTime')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                        <TimePicker
                          name={'mealTime'}
                          required={
                            todayConditionInputFieldsInfo.meal === todayConditionInputFieldUsage.necessary && isMeal
                          }
                          control={control}
                        />
                      </GItem>
                    </GItem>
                  </GContainer>
                </GItem>
              )}
            </>
          )}

        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.mealDetail !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 栄養方法 */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.eatingMethod')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <Select
                      fullWidth
                      name={'eatingMethod'}
                      label={translate('facilityReservationTodayConditionInput.form.label.eatingMethod')}
                      valueLabels={eatingMethodValueLabels}
                      required={todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 朝食を食べましたか */}
              {eatingMethod && eatingMethod !== reservationEatingMethod.brestFeeding && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                      eatingMethod !== null &&
                      eatingMethod !== reservationEatingMethod.brestFeeding && <AttentionLabel>*</AttentionLabel>}
                    {translate('facilityReservationTodayConditionInput.form.label.breakfast')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <Select
                        fullWidth
                        name={'breakfast'}
                        label={translate('facilityReservationTodayConditionInput.form.label.breakfast')}
                        valueLabels={amountOfFoodValueLabels}
                        required={
                          todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}

              {eatingMethod &&
                eatingMethod !== reservationEatingMethod.brestFeeding &&
                breakfast &&
                breakfast !== todayConditionAmountOfFood.none && (
                  <>
                    {/* 朝食時刻 */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding &&
                          breakfast !== null &&
                          breakfast !== todayConditionAmountOfFood.none && <AttentionLabel>*</AttentionLabel>}
                        {translate('facilityReservationTodayConditionInput.form.label.breakfastTime')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                            <TimePicker
                              name={'breakfastTime'}
                              required={
                                todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                                eatingMethod !== null &&
                                eatingMethod !== reservationEatingMethod.brestFeeding &&
                                breakfast !== null &&
                                breakfast !== todayConditionAmountOfFood.none
                              }
                              control={control}
                            />
                          </GItem>
                        </GItem>
                      </GContainer>
                    </GItem>

                    {/* 朝食は何を食べましたか */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding &&
                          breakfast !== null &&
                          breakfast !== todayConditionAmountOfFood.none && <AttentionLabel>*</AttentionLabel>}
                        {translate('facilityReservationTodayConditionInput.form.label.breakfastWhat')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <TextBox
                            name="breakfastWhat"
                            label={translate('facilityReservationTodayConditionInput.form.label.breakfastWhat')}
                            maxLength={200}
                            fullWidth
                            required={
                              todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                              eatingMethod !== null &&
                              eatingMethod !== reservationEatingMethod.brestFeeding &&
                              breakfast !== null &&
                              breakfast !== todayConditionAmountOfFood.none
                            }
                            control={control}
                          />
                        </GItem>
                      </GContainer>
                    </GItem>
                  </>
                )}

              {/* 昼食を食べましたか */}
              {eatingMethod && eatingMethod !== reservationEatingMethod.brestFeeding && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                      eatingMethod !== null &&
                      eatingMethod !== reservationEatingMethod.brestFeeding && <AttentionLabel>*</AttentionLabel>}
                    {translate('facilityReservationTodayConditionInput.form.label.lunch')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <Select
                        fullWidth
                        name={'lunch'}
                        label={translate('facilityReservationTodayConditionInput.form.label.lunch')}
                        valueLabels={amountOfFoodValueLabels}
                        required={
                          todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}

              {eatingMethod &&
                eatingMethod !== reservationEatingMethod.brestFeeding &&
                lunch &&
                lunch !== todayConditionAmountOfFood.none && (
                  <>
                    {/* 昼食時刻 */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding &&
                          lunch !== null &&
                          lunch !== todayConditionAmountOfFood.none && <AttentionLabel>*</AttentionLabel>}
                        {translate('facilityReservationTodayConditionInput.form.label.lunchTime')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                            <TimePicker
                              name={'lunchTime'}
                              required={
                                todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                                eatingMethod !== null &&
                                eatingMethod !== reservationEatingMethod.brestFeeding &&
                                lunch !== null &&
                                lunch !== todayConditionAmountOfFood.none
                              }
                              control={control}
                            />
                          </GItem>
                        </GItem>
                      </GContainer>
                    </GItem>

                    {/* 昼食は何を食べましたか */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding &&
                          lunch !== null &&
                          lunch !== todayConditionAmountOfFood.none && <AttentionLabel>*</AttentionLabel>}
                        {translate('facilityReservationTodayConditionInput.form.label.lunchWhat')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <TextBox
                            name="lunchWhat"
                            label={translate('facilityReservationTodayConditionInput.form.label.lunchWhat')}
                            maxLength={200}
                            fullWidth
                            required={
                              todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                              eatingMethod !== null &&
                              eatingMethod !== reservationEatingMethod.brestFeeding &&
                              lunch !== null &&
                              lunch !== todayConditionAmountOfFood.none
                            }
                            control={control}
                          />
                        </GItem>
                      </GContainer>
                    </GItem>
                  </>
                )}

              {/* おやつは食べましたか */}
              {eatingMethod && eatingMethod !== reservationEatingMethod.brestFeeding && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                      eatingMethod !== null &&
                      eatingMethod !== reservationEatingMethod.brestFeeding && <AttentionLabel>*</AttentionLabel>}
                    {translate('facilityReservationTodayConditionInput.form.label.snack')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem>
                      <RadioButtonGroup
                        name={'snack'}
                        label={translate('facilityReservationTodayConditionInput.form.label.snack')}
                        row
                        required={
                          todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod !== null &&
                          eatingMethod !== reservationEatingMethod.brestFeeding
                        }
                        buttonValueLabels={yesNos}
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}

              {eatingMethod && eatingMethod !== reservationEatingMethod.brestFeeding && snack === '1' && (
                <>
                  {/* おやつ時刻 */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                        eatingMethod !== null &&
                        eatingMethod !== reservationEatingMethod.brestFeeding &&
                        snack === '1' && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.snackTime')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                          <TimePicker
                            name={'snackTime'}
                            required={
                              todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                              eatingMethod !== null &&
                              eatingMethod !== reservationEatingMethod.brestFeeding &&
                              snack === '1'
                            }
                            control={control}
                          />
                        </GItem>
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}

              {/* 栄養方法詳細 */}
              {eatingMethod === reservationEatingMethod.brestFeeding && (
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                      eatingMethod === reservationEatingMethod.brestFeeding && <AttentionLabel>*</AttentionLabel>}
                    {translate('facilityReservationTodayConditionInput.form.label.eatingMethodDetail')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <Select
                        fullWidth
                        name={'eatingMethodDetail'}
                        label={translate('facilityReservationTodayConditionInput.form.label.eatingMethodDetail')}
                        valueLabels={brestFeedingDetailValueLabels}
                        required={
                          todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod === reservationEatingMethod.brestFeeding
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              )}

              {eatingMethod === reservationEatingMethod.brestFeeding &&
                eatingMethodDetail &&
                eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk && (
                  <>
                    {/* ミルク量 */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod === reservationEatingMethod.brestFeeding &&
                          eatingMethodDetail !== null &&
                          eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk && (
                            <AttentionLabel>*</AttentionLabel>
                          )}
                        {translate('facilityReservationTodayConditionInput.form.label.milkAmount.field')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <GContainer alignItems="flex-end">
                            <GItem xs={1}>
                              <NumberBox
                                name={'milkAmount'}
                                label={translate('facilityReservationTodayConditionInput.form.label.milkAmount.field')}
                                max={999}
                                min={1}
                                placeholder={'50'}
                                required={
                                  todayConditionInputFieldsInfo.mealDetail ===
                                    todayConditionInputFieldUsage.necessary &&
                                  eatingMethod === reservationEatingMethod.brestFeeding &&
                                  eatingMethodDetail !== null &&
                                  eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk
                                }
                                control={control}
                              />
                            </GItem>
                            <GItem xs="auto" style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                              {translate('facilityReservationTodayConditionInput.form.label.milkAmount.unit')}
                            </GItem>
                          </GContainer>
                        </GItem>
                      </GContainer>
                    </GItem>

                    {/* ミルク接種時刻 */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod === reservationEatingMethod.brestFeeding &&
                          eatingMethodDetail !== null &&
                          eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk && (
                            <AttentionLabel>*</AttentionLabel>
                          )}
                        {translate('facilityReservationTodayConditionInput.form.label.milkTime')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                            <TimePicker
                              name={'milkTime'}
                              required={
                                todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                                eatingMethod === reservationEatingMethod.brestFeeding &&
                                eatingMethodDetail !== null &&
                                eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk
                              }
                              control={control}
                            />
                          </GItem>
                        </GItem>
                      </GContainer>
                    </GItem>
                  </>
                )}

              {eatingMethod === reservationEatingMethod.brestFeeding &&
                eatingMethodDetail &&
                eatingMethodDetail !== reservationBrestFeedingDetail.formula && (
                  <>
                    {/* 母乳接種時刻 */}
                    <GItem>
                      <TitleLabel>
                        {todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                          eatingMethod === reservationEatingMethod.brestFeeding &&
                          eatingMethodDetail !== null &&
                          eatingMethodDetail !== reservationBrestFeedingDetail.formula && (
                            <AttentionLabel>*</AttentionLabel>
                          )}
                        {translate('facilityReservationTodayConditionInput.form.label.breastMilkTime')}
                      </TitleLabel>
                      <GContainer alignItems="flex-end">
                        <GItem xs={12}>
                          <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                            <TimePicker
                              name={'breastMilkTime'}
                              required={
                                todayConditionInputFieldsInfo.mealDetail === todayConditionInputFieldUsage.necessary &&
                                eatingMethod === reservationEatingMethod.brestFeeding &&
                                eatingMethodDetail !== null &&
                                eatingMethodDetail !== reservationBrestFeedingDetail.formula
                              }
                              control={control}
                            />
                          </GItem>
                        </GItem>
                      </GContainer>
                    </GItem>
                  </>
                )}
            </>
          )}
      </Stack>
    </GItem>
  )
}

export default MealSec
